import { ACCOUNT_SORTS, RETAILER_LABELS, RETAILERS, type EmailAccount } from '@nebueye/shared'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowDown, ArrowUp } from 'lucide-react'
import { z } from 'zod'
import { RetailerMark } from '@/components/app/retailer'
import { SearchBox } from '@/components/app/search-box'
import { EmptyState, ErrorState, OrbitArt } from '@/components/app/states'
import { ColumnHeader, PageTitle, Pager, Pips, RowChevron, Surface, rowClass, rowEnter } from '@/components/app/surface'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { ago, count, percent } from '@/lib/format'
import { accountsQuery } from '@/lib/queries'
import { cn } from '@/lib/utils'

const PAGE = 50
type AccountSort = (typeof ACCOUNT_SORTS)[number]

export const Route = createFileRoute('/_app/accounts')({
  validateSearch: z.object({
    retailer: z.enum(RETAILERS).optional(),
    sort: z.enum(ACCOUNT_SORTS).optional(),
    asc: z.boolean().optional(),
    search: z.string().optional(),
    page: z.number().int().min(1).optional(),
  }),
  head: () => ({ meta: [{ title: 'Email accounts · Nebueye' }] }),
  component: Accounts,
})

/** email · retailer · orders · success · last seen · chevron */
const COLS = 'grid-cols-[minmax(0,1fr)_auto] lg:grid-cols-[minmax(0,1fr)_11rem_15rem_5.5rem_8rem_1.25rem]'

function Accounts() {
  const s = Route.useSearch()
  const navigate = Route.useNavigate()
  const page = s.page ?? 1
  const sort = s.sort ?? 'lastSeen'
  const q = useQuery(
    accountsQuery({ retailer: s.retailer, search: s.search, sort, desc: s.asc ? 'false' : 'true', offset: (page - 1) * PAGE, limit: PAGE }),
  )
  const set = (patch: Partial<typeof s>) => navigate({ search: (p) => ({ ...p, page: undefined, ...patch }), replace: true })
  const onSort = (f: AccountSort) => (f === sort ? set({ asc: s.asc ? undefined : true }) : set({ sort: f === 'lastSeen' ? undefined : f, asc: undefined }))
  const filtered = !!(s.retailer || s.search)
  const data = q.data

  const header = (label: string, field: AccountSort, className?: string) => {
    const active = sort === field
    const Icon = s.asc ? ArrowUp : ArrowDown
    return (
      <button
        type="button"
        onClick={() => onSort(field)}
        aria-label={`Sort by ${label.toLowerCase()}`}
        className={cn('hover:text-foreground inline-flex items-center gap-1 transition-colors', active && 'text-foreground', className)}
      >
        {label}
        {active && <Icon className="size-3.5" />}
      </button>
    )
  }

  return (
    <div className="space-y-6">
      <PageTitle
        title="Email accounts"
        description="Each email address you check out with, and how its orders turned out."
        action={
          <div className="flex w-full items-center gap-2 sm:w-auto">
            <SearchBox
              value={s.search ?? ''}
              onChange={(v) => set({ search: v || undefined })}
              placeholder="Search email accounts"
              label="Search email accounts"
              shortcut
              className="flex-1 sm:w-72 sm:flex-none"
            />
            <Select value={s.retailer ?? 'all'} onValueChange={(v) => set({ retailer: v === 'all' ? undefined : (v as typeof s.retailer) })}>
              <SelectTrigger className="bg-card w-40" aria-label="Retailer">
                <SelectValue />
              </SelectTrigger>
              <SelectContent align="end">
                <SelectItem value="all">All retailers</SelectItem>
                {RETAILERS.map((r) => (
                  <SelectItem key={r} value={r}>
                    {RETAILER_LABELS[r]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        }
      />

      <Surface aria-label="Email accounts">
        <ColumnHeader cols={COLS}>
          <span>Email</span>
          {header('Retailer', 'retailer', 'justify-self-start')}
          {header('Orders', 'orders', 'justify-self-start')}
          {header('Success', 'success', 'justify-self-end')}
          {header('Last seen', 'lastSeen', 'justify-self-end')}
          <span />
        </ColumnHeader>

        {q.isError ? (
          <ErrorState error={q.error} onRetry={() => q.refetch()} className="px-6" />
        ) : !data ? (
          <div aria-busy="true" aria-label="Loading email accounts">
            {Array.from({ length: 10 }, (_, i) => (
              <div key={i} className="flex items-center gap-6 border-b px-5 py-3.5 last:border-b-0">
                <Skeleton className="h-3.5 w-64 max-w-[50%]" />
                <Skeleton className="ml-auto h-3 w-40" />
                <Skeleton className="h-3.5 w-10" />
              </div>
            ))}
          </div>
        ) : data.accounts.length === 0 ? (
          <EmptyState
            className="px-6"
            art={filtered ? undefined : <OrbitArt />}
            title={filtered ? 'No email accounts match' : 'No email accounts yet'}
            action={
              filtered && (
                <Button variant="outline" onClick={() => navigate({ search: {}, replace: true })}>
                  Clear filters
                </Button>
              )
            }
          >
            {filtered
              ? 'Try a different address or retailer.'
              : 'The addresses retailers send your order emails to are listed here, with success and cancel counts for each.'}
          </EmptyState>
        ) : (
          <ul className={cn('transition-opacity duration-150', q.isPlaceholderData && 'opacity-50')}>
            {data.accounts.map((a, i) => (
              <li key={a.id} className="border-b last:border-b-0">
                <AccountRow a={a} index={i} />
              </li>
            ))}
          </ul>
        )}

        {data && <Pager page={page} pageSize={PAGE} total={data.total} noun="email account" onPage={(p) => set({ page: p === 1 ? undefined : p })} />}
      </Surface>
    </div>
  )
}

function AccountRow({ a, index }: { a: EmailAccount; index: number }) {
  const rate = a.orderCount ? a.successCount / a.orderCount : 0
  const enter = rowEnter(index)
  const [local, domain] = a.email.split('@')
  return (
    <Link to="/orders" search={{ search: a.email }} style={enter.style} className={cn(rowClass, enter.className, COLS, 'py-3')}>
      <p className="truncate text-sm">
        <span className="font-medium">{local}</span>
        <span className="text-subtle">@{domain}</span>
      </p>

      <p className="text-muted-foreground hidden items-center gap-2 text-[13px] lg:flex">
        <RetailerMark retailer={a.retailer} className="size-5 rounded text-[8px]" />
        {RETAILER_LABELS[a.retailer]}
      </p>

      <div className="order-3 col-span-2 flex items-center gap-3 lg:order-none lg:col-span-1">
        <RetailerMark retailer={a.retailer} className="size-5 rounded text-[8px] lg:hidden" />
        <p className="tabular w-10 shrink-0 text-sm">
          <span className="font-semibold">{count(a.successCount)}</span>
          <span className="text-subtle">/{count(a.orderCount)}</span>
        </p>
        {a.orderCount > 0 ? <Pips kept={a.successCount} cancelled={a.failedCount} /> : <span className="text-subtle text-[13px]">No orders yet</span>}
        <span className="text-subtle ml-auto text-xs lg:hidden">{ago(a.lastSeenAt)}</span>
      </div>

      <p
        className={cn(
          'tabular text-right text-[15px] font-semibold',
          a.orderCount === 0 ? 'text-subtle' : rate === 1 ? 'text-status-success-text' : rate < 0.5 ? 'text-status-danger-text' : 'text-foreground',
        )}
      >
        {a.orderCount ? percent(rate) : '—'}
      </p>
      <p className="text-muted-foreground hidden text-right text-[13px] whitespace-nowrap lg:block">{ago(a.lastSeenAt)}</p>
      <RowChevron />
    </Link>
  )
}
